import { useState } from 'react';
import { X, FileDown } from 'lucide-react';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (exportType: 'classement' | 'par-agence', company?: string) => void;
  exporting: boolean;
}

export default function ExportModal({ isOpen, onClose, onExport, exporting }: ExportModalProps) {
  const [exportType, setExportType] = useState<'classement' | 'par-agence'>('classement');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');

  if (!isOpen) return null;

  const handleExport = () => {
    if (exportType === 'par-agence') {
      onExport(exportType, selectedCompany);
    } else {
      onExport(exportType);
    }
  };

  const exportByAgency = () => {
    const wb = XLSX.utils.book_new();
    
    // Group negotiators by agency
    const negotiatorsByAgency = new Map<string, Array<{
      negotiator: any;
      total_sales: number;
      potential_sales: number;
      sales_count: number;
      vendeur_count: number;
      apporteur_count: number;
      autre_count: number;
    }>>();

    negotiators.forEach(negotiator => {
      // Get all agencies for this negotiator
      const agencies = negotiator.negotiator_agencies || [];
      
      agencies.forEach(agencyAssoc => {
        const agencyName = agencyAssoc.agency;
        
        if (!negotiatorsByAgency.has(agencyName)) {
          negotiatorsByAgency.set(agencyName, []);
        }

        // Calculer les statistiques pour ce négociateur dans cette agence spécifique
        let agencyTotalSales = 0;
        let agencyPotentialSales = 0;
        let agencySalesCount = 0;
        let agencyVendeurCount = 0;
        let agencyApporteurCount = 0;
        let agencyAutreCount = 0;

        // Filtrer les transactions pour cette agence
        const agencyTransactions = transactions.filter(t => {
          // Vérifier si le négociateur est dans cette transaction
          const negotiatorInTransaction = t.transaction_negotiators?.some(
            tn => tn.negotiator_id === negotiator.id
          );
          
          if (!negotiatorInTransaction) return false;

          // Vérifier si la transaction est liée à cette agence
          const transactionAgency = t.transaction_negotiators?.find(
            tn => tn.negotiator_id === negotiator.id
          )?.agency;

          return transactionAgency === agencyName;
        });

        // Calculer les statistiques pour cette agence
        agencyTransactions.forEach(transaction => {
          const negotiatorData = transaction.transaction_negotiators?.find(
            tn => tn.negotiator_id === negotiator.id
          );

          if (negotiatorData) {
            const totalRetrocessions = transaction.transaction_retrocessions?.reduce(
              (sum, r) => sum + r.amount, 0
            ) || 0;
            const netFeesWithoutTax = transaction.fees_without_tax - totalRetrocessions;
            const negotiatorAmount = (netFeesWithoutTax * negotiatorData.percentage) / 100;

            if (transaction.status === 'potentiel') {
              agencyPotentialSales += negotiatorAmount;
            } else {
              agencyTotalSales += negotiatorAmount;
              agencySalesCount++;

              // Compter les rôles
              if (negotiatorData.role === 'vendeur') {
                agencyVendeurCount++;
              } else if (negotiatorData.role === 'apporteur') {
                agencyApporteurCount++;
              } else {
                agencyAutreCount++;
              }
            }
          }
        });

        negotiatorsByAgency.get(agencyName)!.push({
          negotiator,
          total_sales: agencyTotalSales,
          potential_sales: agencyPotentialSales,
          sales_count: agencySalesCount,
          vendeur_count: agencyVendeurCount,
          apporteur_count: agencyApporteurCount,
          autre_count: agencyAutreCount
        });
      });
    });

    // Sort agencies alphabetically
    const sortedAgencies = Array.from(negotiatorsByAgency.keys()).sort();

    // Create a sheet for each agency
    sortedAgencies.forEach(agencyName => {
      const agencyNegotiators = negotiatorsByAgency.get(agencyName) || [];
      
      // Sort negotiators by total sales (descending)
      const sortedNegotiators = agencyNegotiators.sort((a, b) => 
        b.total_sales - a.total_sales
      );

      const data = sortedNegotiators.map(({ negotiator, total_sales, potential_sales, sales_count, vendeur_count, apporteur_count, autre_count }) => {
        const completion_rate = negotiator.annual_objective 
          ? (total_sales / negotiator.annual_objective) * 100 
          : 0;
        const remaining = negotiator.annual_objective - total_sales;

        return {
          'Agence/Négociateur': `${negotiator.firstname} ${negotiator.lastname}`,
          'Statut': negotiator.status,
          'HONO Potentiel': potential_sales,
          'HONO Cumulé': total_sales,
          'Objectif': negotiator.annual_objective,
          'Reste à réaliser': remaining,
          '% Réalisation': completion_rate,
          '100%': sales_count,
          'VENDEUR': vendeur_count,
          'APPORTEUR': apporteur_count,
          'AUTRE': autre_count
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 25 }, // Agence/Négociateur
        { wch: 12 }, // Statut
        { wch: 15 }, // HONO Potentiel
        { wch: 15 }, // HONO Cumulé
        { wch: 12 }, // Objectif
        { wch: 15 }, // Reste à réaliser
        { wch: 15 }, // % Réalisation
        { wch: 8 },  // 100%
        { wch: 10 }, // VENDEUR
        { wch: 12 }, // APPORTEUR
        { wch: 10 }  // AUTRE
      ];

      XLSX.utils.book_append_sheet(wb, ws, agencyName);
    });

    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const filename = `classement_par_agence_${date}.xlsx`;
    
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Export Excel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Type d'export */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Type d'export
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="exportType"
                  value="classement"
                  checked={exportType === 'classement'}
                  onChange={(e) => setExportType(e.target.value as 'classement')}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Classement</div>
                  <div className="text-sm text-gray-500">Export du classement général</div>
                </div>
              </label>

              <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="exportType"
                  value="par-agence"
                  checked={exportType === 'par-agence'}
                  onChange={(e) => setExportType(e.target.value as 'par-agence')}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Par Agence</div>
                  <div className="text-sm text-gray-500">Export groupé par agence</div>
                </div>
              </label>
            </div>
          </div>

          {/* Sélection de la société (uniquement pour export par agence) */}
          {exportType === 'par-agence' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Société
              </label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Toutes les sociétés</option>
                <option value="KI">KI</option>
                <option value="KA">KA</option>
                <option value="DRYADE">DRYADE</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <FileDown size={18} />
            <span>{exporting ? 'Export en cours...' : 'Exporter'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}